import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Box,
  TablePagination,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import { format } from 'date-fns';

const TherapistActivityLogViewer = ({ token, clientId }) => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10); // Can be less than admin's default
  const [totalLogsCount, setTotalLogsCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // New state for triggering refresh

  const [filters, setFilters] = useState({
    eventType: '',
    startDate: '',
    endDate: ''
    // No userId or actorId filter here as it's scoped to the clientId
  });

  useEffect(() => {
    const fetchLogs = async () => {
      if (!token || !clientId) {
        setLogs([]);
        setTotalLogsCount(0);
        setIsLoading(false);
        // setError("Client ID is required to fetch activity logs."); // Optional: set error if clientId is missing
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const apiPage = page + 1;
        const activeFilters = {};
        for (const key in filters) {
          if (filters[key]) {
            activeFilters[key] = filters[key];
          }
        }
        // Use the therapist-specific API endpoint
        const data = await apiService.getTherapistClientActivityLog(clientId, token, apiPage, rowsPerPage, activeFilters);

        if (data && Array.isArray(data.events)) {
          setLogs(data.events);
          setTotalLogsCount(data.totalEvents || 0);
        } else {
          setLogs([]);
          setTotalLogsCount(0);
          setError('Received unexpected data format from server.');
        }
      } catch (err) {
        setLogs([]);
        setTotalLogsCount(0);
        setError(err.message || 'Failed to fetch client activity logs.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchLogs();
  }, [token, clientId, page, rowsPerPage, filters, refreshTrigger]); // Add refreshTrigger to dependencies

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
    setPage(0); 
  };
  
  const handleClearFilters = () => {
    setFilters({ eventType: '', startDate: '', endDate: '' });
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Event types can be the same as admin, or a subset if desired
  const eventTypes = [
    'USER_LOGIN', 'USER_LOGIN_FAILURE', // Client specific
    'MESSAGE_SENT', 'REVIEW_REQUESTED', 
    'GOAL_CREATED', 'GOAL_UPDATED', 'GOAL_DELETED',
    'JOURNAL_CREATED', 'JOURNAL_UPDATED', 'JOURNAL_DELETED', 
    'SESSION_STARTED', 'SESSION_ENDED',
    'PHASE_CHANGED', // If relevant to client view
    'CLIENT_SETTINGS_UPDATED' // If therapist updated them
    // Exclude admin-only events like USER_ROLE_CHANGED, PLATFORM_SETTINGS_UPDATED etc.
  ].sort();

  if (!clientId) {
    return <Alert severity="warning">No client selected to view activity for.</Alert>;
  }
  
  if (isLoading && logs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading client activity logs...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2, p:2 }}>
      <Typography variant="h6" component="div" sx={{ mb: 2 }}>
        Client Activity Log
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Event Type</InputLabel>
            <Select name="eventType" value={filters.eventType} label="Event Type" onChange={handleFilterChange}>
              <MenuItem value=""><em>All Types</em></MenuItem>
              {eventTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField name="startDate" label="Start Date" type="date" value={filters.startDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} fullWidth size="small"/>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField name="endDate" label="End Date" type="date" value={filters.endDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} fullWidth size="small"/>
        </Grid>
        <Grid item xs={12} sm={2} display="flex" alignItems="center" gap={1}>
            <Button variant="outlined" onClick={handleClearFilters} size="small">Clear</Button>
            <Button variant="contained" onClick={() => setRefreshTrigger(prev => prev + 1)} size="small">Refresh</Button>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{my:2}}>{error}</Alert>}
      
      <TableContainer sx={{ maxHeight: 440 }}> {/* Adjust maxHeight as needed */}
        <Table stickyHeader aria-label="client activity log table">
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>Event Type</TableCell>
              <TableCell>Actor (Email)</TableCell> {/* User is implied to be the client, show actor */}
              <TableCell>IP Address</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && logs.length > 0 && (
              <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!isLoading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No activity logs found for this client with the current filters.
                </TableCell>
              </TableRow>
            )}
            {logs.map((log) => (
              <TableRow hover role="checkbox" tabIndex={-1} key={log._id}>
                <TableCell>
                  {log.timestamp ? format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss') : 'N/A'}
                </TableCell>
                <TableCell>{log.eventType}</TableCell>
                {/* For therapist view, actor is more relevant if it's not the client themselves */}
                <TableCell>
                  {log.actorId ? (
                    <>
                      {log.actorId.name}
                      <br />
                      <Typography variant="caption" color="textSecondary">({log.actorId.email || log.actorId._id})</Typography>
                    </>
                  ) : (log.userId?._id?.toString() === clientId ? 'Client (Self)' : 'System/N/A')}
                </TableCell>
                <TableCell>{log.ipAddress || 'N/A'}</TableCell>
                <TableCell sx={{maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                  <pre style={{margin:0, whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>{log.details ? JSON.stringify(log.details, null, 2) : 'N/A'}</pre>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalLogsCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default TherapistActivityLogViewer;
